import React, { useEffect } from 'react'
import { useRouter } from 'next/router'
import NextImage from 'next/image'
import Link from 'next/link'

import { historyUtils } from '@wartek-id/toolbox-helper'
import { Text } from '@wartek-id/text'
import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast'
import { Spinner } from '@wartek-id/spinner'
import { Sheet, SheetContent, SheetOverlay } from '@wartek-id/sheet-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'

import {
  useMutateAPIAcceptKSInvitation,
  useQueryAPIGetKSSelectionCandidate,
} from 'api/bcks/useAPIBCKSInvitation'
import { useAuth } from 'utils/auth'
import { SELECTION_STATES_FINAL_PAGE } from 'app/BCKSPage/constants'
import { getDayToDeadlineState } from '../utils/date'
import { SelectionStepsV2 } from '../components/SelectionSteps/SelectionStepsV2'
import useKSPSTrackEvent from '../hooks/useKSPSTrackEvent'
import Header from 'app/BCKSPage/components/Header'
import AndroidHandler from 'utils/androidHandler'
import { useBCKSDesktopView } from 'app/BCKSPage/hooks/useBCKSDesktopView'
import { BCKSDesktopPageShell } from 'app/BCKSPage/components/BCKSDesktopPageShell'

import type { ResponseSuccessKSSelectionCandidate } from 'api/bcks/useAPIBCKSInvitation'

export const InvitationConfirmationSheet = (props) => {
  const { onSheetClose, onProceedClick, isLoading } = props
  const { KSPSTrackEvent } = useKSPSTrackEvent()

  return (
    <>
      <div className="flex flex-col ">
        <Text variant="heading-sm" className=" mb-1">
          Konfirmasi persetujuan
        </Text>
        <Text variant="body-sm" color="subdued" className="my-2">
          Dengan memilih <strong>Saya setuju</strong>, berarti Anda akan
          mengikuti seluruh tahapan seleksi Kepala Sekolah dan bersedia menerima
          penempatan yang diputuskan oleh Dinas.
        </Text>
      </div>
      <Button
        className="my-2"
        color="black"
        fullWidth
        onClick={() => {
          onProceedClick()
          KSPSTrackEvent({
            event_name: 'click_ks_selection_invitation_confirm',
          })
        }}
        isLoading={isLoading}
      >
        Saya setuju
      </Button>
      <Button
        color="white"
        className="mb-2"
        fullWidth
        disabled={isLoading}
        onClick={() => onSheetClose(false)}
      >
        Kembali
      </Button>
    </>
  )
}

export const InvitationPage = () => {
  const { session } = useAuth()
  const androidHandler = AndroidHandler()
  const { KSPSTrackEvent } = useKSPSTrackEvent()
  const [isObsoleteInvitation, setIsObsoleteInvitation] = React.useState(false)
  const [sheetOpen, setSheetOpen] = React.useState(false)
  const [candidateInvitationState, setCandidateInvitationState] =
    React.useState<ResponseSuccessKSSelectionCandidate>({
      deadlineDate: null,
      endDate: null,
      invitationFileUrl: null,
      invitationText: null,
      selectionCandidateState: null,
      selectionState: null,
      selectionType: null,
      selectionId: null,
      startDate: null,
      totalCandidate: null,
      schoolName: null,
      schoolCity: null,
      reason: null,
      selectionRegionName: null,
    })

  const { user } = session

  const acceptInviteMutation = useMutateAPIAcceptKSInvitation(
    candidateInvitationState.selectionId
  )
  const { data, isLoading } = useQueryAPIGetKSSelectionCandidate({
    useErrorBoundary: true, // Prevent infinite retry upon failure
  })

  const handleClickBack = () => {
    if (androidHandler.enabled) historyUtils.backToApp(router.back)
    else router.back()
  }

  React.useEffect(() => {
    if (!user) {
      if (androidHandler.enabled)
        historyUtils.backToApp(() => router.replace('/home'))
      else router.replace('/home')
    }

    if (!!data) {
      setCandidateInvitationState(data)

      if (SELECTION_STATES_FINAL_PAGE.includes(data.selectionCandidateState)) {
        router.replace('/bcks/invitation/final')
        return
      }

      const deadlineState = getDayToDeadlineState(data.deadlineDate)

      if (
        deadlineState === 'withinDeadline' &&
        data.selectionCandidateState === 'documentSubmission'
      ) {
        router.replace('/bcks/invitation/precollection')
        return
      }

      if (deadlineState === 'exceedDeadline') {
        setIsObsoleteInvitation(true)
        return
      }

      // Otherwise, redirect to home
      if (data.selectionCandidateState !== 'invitation') {
        handleClickBack()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, user])

  const toast = useToast()
  const router = useRouter()

  const handleAcceptInvitation = () => {
    // Do nothing if there is any current submission
    if (acceptInviteMutation.isLoading) return

    acceptInviteMutation.mutate(candidateInvitationState.selectionId, {
      onSuccess: () => {
        router.replace('/bcks/invitation/precollection')
      },
      onError: () => {
        // Dismiss the sheet
        setSheetOpen(false)

        // Display error message
        toast({
          message: 'Gagal menerima undangan',
        })
      },
    })
  }

  useEffect(() => {
    KSPSTrackEvent({
      event_name: 'enter_ks_selection_invitation_page',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { isDesktop } = useBCKSDesktopView()

  if (
    isDesktop &&
    (isLoading || data?.selectionCandidateState !== 'invitation')
  ) {
    return (
      <BCKSDesktopPageShell
        title="Undangan Seleksi/Pelatihan BCKS"
        pageLabel="Undangan Seleksi/Pelatihan BCKS"
      >
        <div className="bg-white rounded-lg border border-subdued p-6 flex flex-col items-center justify-center py-16">
          <Spinner size="lg" />
          <Text className="mt-4" variant="body">
            Halaman sedang dimuat...
          </Text>
        </div>
      </BCKSDesktopPageShell>
    )
  }

  if (isDesktop) {
    return (
      <BCKSDesktopPageShell
        title="Undangan Seleksi/Pelatihan BCKS"
        pageLabel="Undangan Seleksi/Pelatihan BCKS"
      >
        {isObsoleteInvitation && (
          <div className="bg-surface-selected-default rounded-lg p-4 mb-4">
            <Text variant="body-sm">
              Maaf, waktu Pengumpulan Berkas sudah lewat sehingga Anda tidak
              bisa mengikuti seleksi di periode ini.
            </Text>
          </div>
        )}

        <div className="flex gap-6 items-start">
          {/* Left column — invitation context (sticky) */}
          <div className="w-full max-w-md shrink-0 space-y-4 sticky top-24 self-start">
            {/* Greeting card */}
            <div className="bg-white rounded-lg border border-subdued p-6">
              <div className="mb-4">
                <Text>Yth. Bapak/Ibu {user.name}</Text>
                <Text>Guru {candidateInvitationState.schoolName}</Text>
                <Text>{candidateInvitationState.schoolCity}</Text>
              </div>
              <Text>
                Kami mengundang Anda untuk mengikuti Seleksi/Pelatihan BCKS.
                Silakan respon di bagian paling akhir halaman ini.
              </Text>
            </div>

            {/* Invitation document card */}
            <div className="bg-white rounded-lg border border-subdued p-6">
              <Text variant="heading-sm" className="mb-4">
                Undangan
              </Text>
              <div className="flex gap-4 items-center">
                <a
                  target="_blank"
                  href={candidateInvitationState.invitationFileUrl}
                  rel="noreferrer"
                  className="flex !shrink-0"
                >
                  <NextImage
                    src="/images/bcks/img-invitation-doc.png"
                    alt={'image-placeholder'}
                    width={72}
                    height={72}
                  />
                </a>
                <div>
                  <Text variant="body">
                    Dokumen undangan Seleksi/Pelatihan BCKS
                  </Text>
                  <Link
                    href={`/bcks/pdf-preview?title=%20Surat%20Resmi%20Undangan%20Seleksi%20Kepala%20Sekolah&url=${candidateInvitationState.invitationFileUrl}`}
                  >
                    <Button
                      variant="ghost"
                      className="pt-2 !px-0"
                      onClick={() => {
                        KSPSTrackEvent({
                          event_name:
                            'click_ks_selection_invitation_see_invitation',
                        })
                      }}
                    >
                      Lihat Dokumen
                      <Icon color="informational">chevron_right</Icon>
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Disclaimer + CTA card */}
            <div className="bg-white rounded-lg border border-subdued p-6">
              <Text variant="heading-sm">Penting diketahui:</Text>
              <Text className="mt-1">
                Jika ditemukan ketidaksesuaian data berdasarkan regulasi yang
                berlaku, maka seluruh proses dapat dibatalkan.
              </Text>
              <Button
                fullWidth
                size="lg"
                className="mt-6"
                disabled={isObsoleteInvitation}
                onClick={() => {
                  setSheetOpen(true)
                  KSPSTrackEvent({
                    event_name: 'click_ks_selection_invitation_accept',
                  })
                }}
              >
                Terima Undangan
              </Button>
            </div>
          </div>

          {/* Right column — selection steps */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-lg border border-subdued overflow-hidden p-6">
              <Text variant="heading-sm">
                Tahapan yang akan dilalui jika Anda menerima undangan:
              </Text>
              <div className="bg-white">
                <SelectionStepsV2 staticView />
              </div>
            </div>
          </div>
        </div>

        <Dialog isOpen={sheetOpen} onClose={() => setSheetOpen(false)}>
          <DialogOverlay />
          <DialogContent className="max-w-xl m-auto p-4 self-center">
            <InvitationConfirmationSheet
              onProceedClick={() => handleAcceptInvitation()}
              onSheetClose={() => setSheetOpen(false)}
              isLoading={acceptInviteMutation.isLoading}
            />
          </DialogContent>
        </Dialog>
      </BCKSDesktopPageShell>
    )
  }

  if (isLoading || data?.selectionCandidateState !== 'invitation') {
    return (
      <div className="relative flex flex-col h-screen max-w-xl mx-auto items-center">
        <Header
          isSticky
          variant="large"
          title={'Undangan Seleksi/Pelatihan BCKS'}
          withRightActionButton={false}
          onBackClick={handleClickBack}
        />
        <main className="flex flex-col content-around sticky w-full h-full p-2 items-center justify-center">
          <Spinner size="lg" />
          <Text className="mt-4" variant="body">
            Halaman sedang dimuat...
          </Text>
        </main>
      </div>
    )
  }

  return (
    <div className="relative flex flex-col h-screen max-w-xl mx-auto">
      <Header
        isSticky
        variant="large"
        title={'Undangan Seleksi/Pelatihan BCKS'}
        withRightActionButton={false}
        onBackClick={handleClickBack}
      />
      <main>
        {isObsoleteInvitation && (
          <div className="bg-surface-selected-default p-4">
            <Text variant="body-sm">
              Maaf, waktu Pengumpulan Berkas sudah lewat sehingga Anda tidak
              bisa mengikuti seleksi di periode ini.
            </Text>
          </div>
        )}
        <div className="p-4">
          <div className="mb-4">
            <Text>Yth. Bapak/Ibu {user.name}</Text>
            <Text>Guru {candidateInvitationState.schoolName}</Text>
            <Text>{candidateInvitationState.schoolCity}</Text>
          </div>

          <Text>
            Kami mengundang Anda untuk mengikuti Seleksi/Pelatihan BCKS. Silakan
            respon di bagian paling akhir halaman ini.
          </Text>

          <Text variant="heading-sm" className="mt-6">
            <>
              Berikut seluruh tahapan yang akan dilalui jika Anda menerima
              undangan ini:
            </>
          </Text>
        </div>

        <div className="bg-surface-subdued p-4">
          <SelectionStepsV2 staticView />
        </div>

        <div className="pt-6 px-4 pb-5">
          <Text variant="heading-sm">Undangan</Text>
          <div className="flex gap-3 items-start mt-3">
            <a
              target="_blank"
              href={candidateInvitationState.invitationFileUrl}
              rel="noreferrer"
              className="flex !shrink-0"
            >
              <NextImage
                src="/images/bcks/img-invitation-doc.png"
                alt={'image-placeholder'}
                width={72}
                height={72}
              />
            </a>
            <div>
              <Text variant="body">
                Dokumen undangan Seleksi/Pelatihan BCKS
              </Text>

              <Link
                href={`/bcks/pdf-preview?title=%20Surat%20Resmi%20Undangan%20Seleksi%20Kepala%20Sekolah&url=${candidateInvitationState.invitationFileUrl}`}
              >
                <Button
                  variant="ghost"
                  className="pt-2 !px-0"
                  onClick={() => {
                    KSPSTrackEvent({
                      event_name:
                        'click_ks_selection_invitation_see_invitation',
                    })
                  }}
                >
                  Lihat Dokumen
                  <Icon color="informational">chevron_right</Icon>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <footer>
        <div className="py-6 px-4">
          <Text variant="heading-sm">Penting diketahui:</Text>

          <Text className="mt-1">
            Jika ditemukan ketidaksesuaian data berdasarkan regulasi yang
            berlaku, maka seluruh proses dapat dibatalkan.
          </Text>

          <Button
            fullWidth
            size="lg"
            className="mt-6"
            disabled={isObsoleteInvitation}
            onClick={() => {
              setSheetOpen(true)
              KSPSTrackEvent({
                event_name: 'click_ks_selection_invitation_accept',
              })
            }}
          >
            Terima Undangan
          </Button>
        </div>

        <Sheet isOpen={sheetOpen} onClose={setSheetOpen} type="alert">
          <SheetOverlay />
          <SheetContent className="max-w-xl m-auto p-4">
            <InvitationConfirmationSheet
              onProceedClick={() => handleAcceptInvitation()}
              onSheetClose={() => setSheetOpen(false)}
              isLoading={acceptInviteMutation.isLoading}
            />
          </SheetContent>
        </Sheet>
      </footer>
    </div>
  )
}
